import { lazy } from 'react';

import classNames from 'classnames';
import dayjs from 'dayjs';
import { observer } from 'mobx-react';

import { EnumOpenDialog } from '../../../stores/ui/types';
import { useStores } from '../../../stores/useStores';
import { formatTime } from '../../../utils/time';

import TournamentTimer from './components/TournamentTimer';
import styles from './TournamentWidget.module.scss';

const TrophyIcon = lazy(() => import('../../SVGIcons/TrophyIcon'));

const RegularTournamentWidget = observer(function RegularTournamentWidget() {
  const { coinTypeStore, uiStore, tournamentStore } = useStores();

  const tournament = tournamentStore.currentTournament;
  if (!tournament) return null;

  const { status, leaderboards, playerStatus } = tournament;
  const coinType = coinTypeStore.coinType.toLowerCase() as 'gc' | 'sc';
  const leaderboard = leaderboards[coinType];
  if (!leaderboard) return null;

  const isTournamentUpcoming = status === 'upcoming';
  const isTournamentOngoing = status === 'ongoing';
  const isTournamentStartedForPlayer = isTournamentOngoing && playerStatus === 'started';
  const { startDate, endDate, widgetImageUrl, maxPositionDisplay } = tournament.tournamentGroup;
  const showTimer = isTournamentUpcoming || isTournamentOngoing;

  const onTimerEnd = () => {
    const randomDelayMS = (Math.floor(Math.random() * 5) + 1) * 1000;
    setTimeout(() => {
      void tournamentStore.fetch();
    }, randomDelayMS);
  };

  const onClick = () => {
    let timeLeftAnalytics;
    if (showTimer) {
      const destinationTime = isTournamentUpcoming ? startDate : endDate;
      const secondsToDate = dayjs(destinationTime).diff(dayjs(), 'seconds');
      const timer = formatTime(secondsToDate, 's');
      timeLeftAnalytics = { time_left: timer };
    }

    tournamentStore.handleAnalytics({
      event_action: 'impression',
      params: {
        triggered_by: 'click',
        click_location: 'widget',
        ...(timeLeftAnalytics ?? {}),
      },
    });

    if (isTournamentOngoing) {
      void tournamentStore.fetch();
    }

    uiStore.setOpenDialog({
      dialog: EnumOpenDialog.isOpenTournamentDialog,
      options: { shown_by: 'widget_click' },
    });
  };

  const renderPosition = () => {
    if (!isTournamentStartedForPlayer) return null;
    const playerPosition = leaderboard.player.position + 1;
    if (playerPosition > maxPositionDisplay) {
      return <div className={styles.position}>{maxPositionDisplay}+</div>;
    }
    return (
      <div className={styles.position} data-testid="my-rank">
        #{playerPosition}
      </div>
    );
  };

  return (
    <div className="home-widget" data-testid="tournament-widget">
      <div
        className={classNames(styles.tournamentWidget, 'circle-parent')}
        data-testid="regular-tournament-widget-button"
        onClick={onClick}
      >
        <div className={styles.contentWrapper}>
          <div className={styles.outerCircle}>
            <div className={styles.innerCircle} />
          </div>
          {widgetImageUrl ? (
            <img className={styles.trophyIcon} loading="lazy" src={widgetImageUrl} />
          ) : (
            <TrophyIcon className={styles.trophyIcon} />
          )}
        </div>
        {showTimer && (
          <div className={styles.bottomSection}>
            {renderPosition()}
            <TournamentTimer
              isOnWidget
              className="widget-timer"
              endDate={endDate}
              startDate={startDate}
              theme="black"
              timerFormat="short"
              to={isTournamentUpcoming ? 'start' : 'end'}
              onComplete={onTimerEnd}
            />
          </div>
        )}
      </div>
    </div>
  );
});

export default RegularTournamentWidget;
